import { z } from "zod/v4";
import { fileSortSchema } from "../../llm/files/types";

/** Validates the user's style selection for the current message. */
export const styleContextSchema = z.discriminatedUnion("mode", [
  z
    .object({ mode: z.literal("auto") })
    .describe(
      "The user left style selection on Auto. Call list_styles for the brand profile and apply the most relevant style, even when the fit is partial. Build without one only when none plausibly fits, or the request conflicts with all of them, for example by setting its own art direction or mirroring a reference ad. Read the chosen style with read_style, pass its reference images to image generation, and carry its guidance into the ad. Once a style is chosen, set ads.style to it in the context of every subagent that works on the request, so the whole set uses one style. If the user names a different style, use that one.",
    ),
  z
    .object({ mode: z.literal("none") })
    .describe(
      "The user chose no style. Use a style only when the user names one, asks which styles exist, or asks for ads in a particular style. Find it with list_styles and read it with read_style.",
    ),
  z
    .object({
      mode: z.literal("style"),
      id: z.uuid().describe("Style ID to pass to read_style."),
      name: z.string().min(1),
    })
    .describe(
      "The user selected this style. Read it with read_style before generating and treat it as the default creative direction. Cover the document: pass references from each part that has images and applies to the ad. Pass several when a part's images reinforce one intent, and pick one when they are alternatives, keeping that choice across a campaign. Carry the text around each image into the guidance of its reference, keeping firm requirements firm. When the user's instructions conflict with the style, follow the user and apply only the parts of the style that do not conflict.",
    ),
]);

/** The user's style selection for the current message. */
export type StyleContext = z.infer<typeof styleContextSchema>;

const namedItem = z.object({
  id: z.string().min(1).describe("The id."),
  name: z.string().describe("The name that the user sees."),
});

const canvasFile = z.object({
  nodeId: z.string().min(1).describe("The id of the canvas node of the file."),
  fileId: z.string().min(1).describe("The id of the file."),
  name: z.string().nullable().describe("The file name, or null without one."),
  type: z.string().describe("The file type."),
});

const emailComponent = z.object({
  id: z.string().min(1).describe("The id of the email component."),
  name: z.string().describe("The name that the user sees."),
  componentType: z
    .string()
    .nullable()
    .describe("The type of the component, or null when it is unknown."),
});

const styleSelection = z.union([namedItem, z.literal("none")]);

const resizeSize = z.object({
  channelSizeId: z.string().min(1).describe("The channel size to create."),
  width: z.number().positive().describe("The width in pixels."),
  height: z.number().positive().describe("The height in pixels."),
});

export const adResizeContextSchema = z.object({
  assetId: z.string().min(1).describe("The source ad to resize."),
  fileId: z
    .string()
    .min(1)
    .optional()
    .describe("The source ad's Library file id."),
  sizes: z
    .array(
      z.union([
        resizeSize.extend({
          channel: z.string().describe("The destination channel."),
          label: z.string().describe("The size name."),
        }),
        resizeSize.extend({
          template: z
            .object({
              entityId: z.string().min(1),
              entityVersionId: z.string().min(1),
              name: z.string(),
            })
            .describe("The template to use for this size."),
        }),
      ]),
    )
    .describe("The requested output sizes."),
});
export type AdResizeContext = z.infer<typeof adResizeContextSchema>;

export const adsContextSchema = z.object({
  style: styleContextSchema
    .optional()
    .describe("The user's ad style selection. Absent means none."),
  templates: z
    .array(namedItem)
    .optional()
    .describe("The templates for the ads that this message starts."),
  creativeBoost: z
    .boolean()
    .optional()
    .describe("Use visual research for the ads this message requests."),
  useVideoEngine: z
    .boolean()
    .optional()
    .describe("Use the selected video generation engine for this message."),
  resize: adResizeContextSchema
    .optional()
    .describe("The ad resize request for this message."),
});
export type AdsContext = z.infer<typeof adsContextSchema>;

export const emailsContextSchema = z.object({
  style: styleSelection
    .optional()
    .describe(
      'The style for the emails that this message starts, or "none" for no style. Absent means you choose.',
    ),
  templates: z
    .array(namedItem)
    .optional()
    .describe("The templates for the emails that this message starts."),
  components: z
    .array(emailComponent)
    .optional()
    .describe("The email components to use in those emails."),
});
export type EmailsContext = z.infer<typeof emailsContextSchema>;

/** The canvas the user is viewing. */
export const canvasContextSchema = z.object({
  canvasId: z.string().min(1).describe("The id of the open canvas."),
  name: z.string().describe("The name of the canvas."),
  brandProfileId: z
    .string()
    .min(1)
    .nullable()
    .describe("The brand profile of the canvas, or null without a brand."),
  selectedFiles: z
    .array(canvasFile)
    .describe("The files that the user selected on the canvas."),
  openFile: canvasFile
    .nullable()
    .describe("The file open in an editor over the canvas, or null."),
});

export const libraryContextSchema = z.object({
  folderId: z
    .string()
    .nullable()
    .describe("The open folder, or null for all files."),
  folderPath: z.string().nullable().describe("The path of the open folder."),
  drafts: z.boolean().describe("Whether Drafts is open."),
  favorites: z.boolean().describe("Whether Favorites is open."),
  mine: z.boolean().describe("Whether My files is open."),
  fileTypes: z
    .array(z.string())
    .describe("Visible file types; empty for all types."),
  brandProfileIds: z
    .array(z.string())
    .describe("Visible brands; empty for all brands."),
  search: z.string().describe("The file name search; empty for no search."),
  includeHidden: z
    .boolean()
    .describe("Whether staff has included hidden files."),
  sort: fileSortSchema.describe("The ordering of files in the library."),
  selectedFiles: z
    .array(canvasFile.omit({ nodeId: true }))
    .describe("Files selected for a bulk action."),
  openFile: canvasFile
    .omit({ nodeId: true })
    .nullable()
    .describe("The file open in an editor, or null."),
});

export type CanvasContext = z.infer<typeof canvasContextSchema>;
export type LibraryContext = z.infer<typeof libraryContextSchema>;
export const projectContextSchema = z.object({
  projectId: z.string().describe("The id of the open project."),
  projectTitle: z.string().optional().describe("The title of the project."),
  brandProfileId: z.string().nullable().describe("The project brand, or null."),
});

export const taskContextSchema = projectContextSchema.extend({
  taskId: z.string().describe("The id of the open task."),
  taskTitle: z.string().optional().describe("The title of the task."),
});

export const PAGE_URL_MAX_LENGTH = 2048;
export const PAGE_TITLE_MAX_LENGTH = 256;

/** The app page that the user sent the message from. */
export const pageContextSchema = z.object({
  url: z
    .string()
    .max(PAGE_URL_MAX_LENGTH)
    .describe("The URL of the page in the Hightouch app."),
  title: z
    .string()
    .max(PAGE_TITLE_MAX_LENGTH)
    .describe("The title of the page, as shown in the browser tab."),
});
export type PageContext = z.infer<typeof pageContextSchema>;

export const contextHubContextSchema = z.object({
  selectedItems: z
    .array(
      z.object({
        kind: z.literal("adTemplate"),
        id: z.string().min(1),
      }),
    )
    .describe(
      "The items selected in Context Hub. Empty when nothing is selected.",
    ),
});
export type ContextHubContext = z.infer<typeof contextHubContextSchema>;

export const ampAgentContextSchema = z.object({
  contextHub: contextHubContextSchema
    .optional()
    .describe(
      "The user's selection in Context Hub, identifying items they are referring to.",
    ),
  page: pageContextSchema
    .optional()
    .describe(
      "The app page that the user sent this message from. Use it to orient; where other context keys describe the same thing, prefer them, especially for ids.",
    ),
  project: projectContextSchema
    .optional()
    .describe("The project the user is viewing."),
  task: taskContextSchema.optional().describe("The task the user is viewing."),
  canvas: canvasContextSchema
    .optional()
    .describe("The canvas the user is viewing."),
  library: libraryContextSchema
    .optional()
    .describe("The user's current view of the asset library."),
  ads: adsContextSchema
    .optional()
    .describe("The user's ad generation choices for this message."),
  emails: emailsContextSchema
    .optional()
    .describe("The user's email generation choices for this message."),
  selectedSkills: z
    .array(namedItem)
    .optional()
    .describe("Knowledge skills explicitly selected for this message."),
});
export type AmpAgentContext = z.infer<typeof ampAgentContextSchema>;

export const AMP_AGENT_CONTEXT = ampAgentContextSchema.keyof().enum;
